import React, { type JSX } from 'react';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { OverviewBlock } from '@/components/OverviewBlock';
import { ProConBlock } from '@/components/ProConBlock';
import { BodyObject } from '../../types/api';
import { PinterestImage } from '../PinterestImage';
import { ScrollableElementContainer } from '../ScrollableElementContainer';
import { SectionHeading } from '../SectionHeading';
import { InstagramPostEmbed } from '../InstagramPostEmbed';
import { DailyWagArticleBlockquote } from '../../containers/DailyWagArticlePageContainer/components/DailyWagArticleBodyContainer/components/DailyWagArticleBlockquote';
import { DailyWagArticleContentContainer } from '../../containers/DailyWagArticlePageContainer/components/DailyWagArticleBodyContainer/components/DailyWagArticleContentContainer';
import { PurchaseCtaValue, SummaryBodyObject } from '../../types/api/affiliateProductReviewArticle';
import { VideoLightbox } from '../../wag-react/next-components/video-lightbox/VideoLightbox';
import { Typography } from '../../wag-react/components/Typography';
import { Image } from '../../wag-react/next-components/image';
import { Flex } from '../../wag-react/next-components/Flex';
import { Button } from '../../wag-react/next-components/button/Button';
import { Box, BoxProps } from '../../wag-react/next-components/box';
import { theme } from '../../wag-react/themes/base-theme';
export type ImageAndAltTextProps = {
  containerProps: BoxProps;
};
export type PurchaseCtasSectionProps = {
  containerProps: BoxProps;
};
export type ItemProps = {
  item: BodyObject | SummaryBodyObject;
  imageAndAltTextProps?: ImageAndAltTextProps;
  purchaseCtasSectionProps?: PurchaseCtasSectionProps;
  prosConsSectionPops?: BoxProps;
  imageLoading?: 'eager' | 'lazy';
};
export const BodyContainerComponentsMapping = ({
  // Do not structure as it will lose the discriminated union from TypeScript
  item,
  imageAndAltTextProps,
  purchaseCtasSectionProps,
  prosConsSectionPops,
  // Eager is the default browser behavior
  imageLoading = 'eager'
}: ItemProps): JSX.Element | null => {
  /* Render different components
  based on the  api response */
  if (item.type === 'overview') {
    return <OverviewBlock value={item.value} />;
  }
  if (item.type === 'header') {
    return <ScrollableElementContainer id={item.id}>
        <SectionHeading color="#474747" variant="h2" textAlign="left">
          {item.value}
        </SectionHeading>
      </ScrollableElementContainer>;
  }
  if (item.type === 'subheader') {
    return <ScrollableElementContainer id={item.id}>
        <SectionHeading display="grid" justifySelf="start" textAlign="left" color="#474747" variant="h3" my="0px" py="0px">
          {item.value}
        </SectionHeading>
      </ScrollableElementContainer>;
  }
  if (item.type === 'paragraph') {
    return <DailyWagArticleContentContainer value={item.value} />;
  }
  if (item.type === 'video_and_image') {
    return <VideoLightbox thumbnailSet="" thumbnail={item.thumbnail} width="100%" src={item.videoLink} thumbnailProps={{
      src: item.thumbnail,
      imageProps: {
        borderRadius: '8px'
      }
    }}
    // No text needed as per design
    text="" />;
  }
  if (item.type === 'image' || item.type === 'image_v2') {
    return <Image width={1} borderRadius="8px" my="40px" src={item.image} alt={item.altText || item.alt_text} loading={imageLoading} />;
  }
  if (item.type === 'image_and_alt_text') {
    return <Box display="inline-block" textAlign="center" my={['20px', '40px']} {...imageAndAltTextProps && {
      ...imageAndAltTextProps.containerProps
    }}>
        <Image width={1} borderRadius="8px" src={item.image} alt={item.altText} />

        {item?.altText && <Typography variant="legal" mt="4px">
            {item?.altText}
          </Typography>}
      </Box>;
  }
  if (item.type === 'infographic') {
    return <PinterestImage url={item.image} imageProps={{
      alt: item.altText,
      width: ['344px', '600px'],
      height: ['344px', '600px']
    }} />;
  }
  if (item.type === 'blockquote') {
    return <DailyWagArticleBlockquote value={item.value} />;
  }
  if (item.type === 'tweet') {
    return <TwitterTweetEmbed tweetId={item.value} />;
  }
  if (item.type === 'instagram') {
    return <InstagramPostEmbed value={item.value} />;
  }
  if (item.type === 'pros_cons_section') {
    return <Flex flexDirection={['column', 'row']} {...prosConsSectionPops}>
        {Array.isArray(item.value) && item.value.map(proConBlock => <ProConBlock key={String(proConBlock)} value={proConBlock.value} mr={[0, '10px']} mt={['12px', 0]} mb={prosConsSectionPops ? [0, 2] : 0} />)}
      </Flex>;
  }
  if (item.type === 'purchase_ctas_section') {
    return <Box display={['block', 'inline-block']} textAlign="right" {...purchaseCtasSectionProps && {
      ...purchaseCtasSectionProps.containerProps
    }}>

        {Array.isArray(item.value) && (item.value as PurchaseCtaValue[]).map(({
        text,
        url,
        isFeatured
      }) => <>
            {isFeatured && item.value.length > 1 && <Box display="inline-block" bg={theme.colors.mySin} borderRadius="4px" p="4px 8px" mb="8px">
                <Typography variant="body2" color={theme.colors.white} fontSize="14px" fontWeight={700} textTransform="uppercase">
                  {`Editor's Choice`}
                </Typography>
              </Box>}

            <Button href={url} target="_blank" variant={isFeatured ? 'contained' : 'outlined'} display="flex" mb="12px" maxWidth={['auto', '350px']} innerProps={{
          justifyContent: 'center'
        }}>
              {text}
            </Button>
          </>)}
      </Box>;
  }
  return null;
};